import { useEffect, useMemo, useState, useRef } from 'react';
import { CheckCircle, X } from '@phosphor-icons/react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { useToast } from '@/hooks/use-toast';
import { useInfiniteSearchContacts } from '@/modules/contacts/hooks/use-contacts';
import type { Contact } from '@/modules/contacts/types/contacts.types';
import { useTestSendCampaignRest } from '../../hooks/use-campaigns-rest';

const SAMPLE_CONTACT_RESULTS = 5;

const contactLabel = (contact: Contact) => {
  const name = [contact.FirstName, contact.LastName].filter(Boolean).join(' ').trim();
  return name ? `${name} · ${contact.Email ?? ''}` : contact.Email ?? contact.ItemId;
};

const emailSchema = z.string().trim().email();

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `test-send-${Date.now()}-${Math.random().toString(36).slice(2)}`;

type CampaignTestSendDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId?: string;
  subject?: string;
};

export const CampaignTestSendDialog = ({
  open,
  onOpenChange,
  campaignId,
  subject = '',
}: CampaignTestSendDialogProps) => {
  const { toast } = useToast();
  const { mutateAsync: testSend, isPending: sending } = useTestSendCampaignRest();
  const [recipients, setRecipients] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState<string[]>([]);
  /** Optional real contact the merge tags render against; the server's sample contact otherwise. */
  const [sampleContact, setSampleContact] = useState<Contact | null>(null);
  const [contactQuery, setContactQuery] = useState('');
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  const contactSearch = useInfiniteSearchContacts(contactQuery, {
    pageSize: SAMPLE_CONTACT_RESULTS,
    enabled: open && !sampleContact && contactQuery.trim().length >= 2,
  });
  const contactMatches = useMemo(
    () => contactSearch.data?.pages[0]?.items ?? [],
    [contactSearch.data]
  );

  useEffect(() => {
    if (!open) return;
    setRecipients([]);
    setDraft('');
    setError('');
    setAccepted([]);
    setSampleContact(null);
    setContactQuery('');
    idempotencyKeyRef.current = newIdempotencyKey();
  }, [open]);

  const addRecipient = () => {
    const value = draft.trim().replace(/,$/, '');
    if (!value) return;
    if (recipients.length >= 5) {
      setError('Up to 5 test recipients.');
      return;
    }
    if (!emailSchema.safeParse(value).success) {
      setError('That doesn’t look like a valid email address.');
      return;
    }
    if (recipients.includes(value.toLowerCase())) {
      setError('That address is already in the list.');
      return;
    }
    setError('');
    setRecipients((prev) => [...prev, value.toLowerCase()]);
    setDraft('');
  };

  const handleSend = async () => {
    if (!recipients.length) {
      setError('Add at least one recipient.');
      return;
    }
    if (!subject.trim()) {
      setError('This campaign has no subject line yet — set one on the Details step first.');
      return;
    }
    if (!campaignId?.trim()) {
      setError('Save campaign details before sending a test.');
      return;
    }

    setError('');
    try {
      const result = await testSend({
        campaignId,
        recipients,
        idempotencyKey: idempotencyKeyRef.current,
        contactId: sampleContact?.ItemId,
      });
      setAccepted(result.recipients);
      toast({
        variant: 'success',
        title: 'Test send accepted',
        description: `Marked [TEST] for ${result.accepted} recipient${result.accepted === 1 ? '' : 's'}.`,
      });
    } catch (sendError) {
      toast({
        variant: 'destructive',
        title: 'Test send failed',
        description: sendError instanceof Error ? sendError.message : 'Please try again.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Send a test</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          Sends a real email marked <strong>[TEST]</strong> to up to 5 addresses. Test sends bypass
          subscription and suppression gating for the named recipients, and are excluded from this
          campaign’s stats. The footer’s unsubscribe link opens the real page but unsubscribes no
          one.
        </p>

        <div className="space-y-2">
          <Label htmlFor="campaign-test-send-input">Recipients</Label>
          {recipients.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {recipients.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs"
                >
                  {email}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${email}`}
                    onClick={() => {
                      setRecipients((prev) => prev.filter((item) => item !== email));
                      // The "up to 5" / duplicate message no longer applies once the list changes.
                      setError('');
                    }}
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <Input
            id="campaign-test-send-input"
            type="email"
            placeholder="Type an email and press Enter"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                addRecipient();
              }
            }}
          />
          {error ? (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-test-send-contact">Render merge tags as (optional)</Label>
          {sampleContact ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs">
              {contactLabel(sampleContact)}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label="Use the sample contact instead"
                onClick={() => setSampleContact(null)}
              >
                <X className="size-3.5" />
              </button>
            </span>
          ) : (
            <>
              <Input
                id="campaign-test-send-contact"
                placeholder="Search a contact by name or email"
                value={contactQuery}
                onChange={(event) => setContactQuery(event.target.value)}
              />
              {contactQuery.trim().length >= 2 && contactMatches.length > 0 ? (
                <ul className="max-h-40 overflow-auto rounded-md border border-border text-xs">
                  {contactMatches.map((contact) => (
                    <li key={contact.ItemId}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-muted/60"
                        onClick={() => {
                          setSampleContact(contact);
                          setContactQuery('');
                        }}
                      >
                        {contactLabel(contact)}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Without one, merge tags use a sample contact.
              </p>
            </>
          )}
        </div>

        {accepted.length > 0 ? (
          <div className="rounded-lg bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900">
            {accepted.map((email) => (
              <div key={email} className="flex items-center gap-1.5">
                <CheckCircle className="size-3.5" />
                {email} — accepted
              </div>
            ))}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-primary-600 text-white hover:bg-primary-700"
            disabled={sending}
            onClick={() => {
              void handleSend();
            }}
          >
            {sending ? 'Sending…' : 'Send test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
